import { fetchTokenResult } from "@bindery/auth-consumer/browser";
import {
  type PresentedChoice,
  presentRecommendations,
} from "@bindery/shared/reader/daily-reading";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type SessionState,
  type SessionStore,
  sessionStore,
  useSession,
} from "@/convex/session";
import { readerDb } from "@/sync";
import { localReaderOf } from "@/sync/offline-reader";
import {
  type DailyPool,
  type DailyPoolDeps,
  loadDailyPool,
} from "./daily-pool";
import { requestRecommendations } from "./recommendations-api";

/**
 * `choices` is at most MAX_PRESENTED_CHOICES recommendations, best first,
 * with `{ kind: "starter_worksheet" }` last whenever fewer than
 * STARTER_WORKSHEET_BELOW_RECOMMENDATIONS survive (`starterWorksheetBook`
 * in ./default-library). A signed-out caller is ready with the worksheet
 * alone. `offline`: the server could not be reached, so `choices` come
 * from the last pool this device held. `refreshing`: a `refresh()` fetch
 * is in flight; `choices` still show the previous pool until it settles.
 */
export type DailyRecommendationsState =
  | { status: "loading" }
  | {
      status: "ready";
      choices: PresentedChoice[];
      offline: boolean;
      refreshing: boolean;
    };

/**
 * `refresh` fetches a new pool even when the cached one is fresh; calling
 * it while a refresh is already in flight does nothing.
 */
export type DailyRecommendations = DailyRecommendationsState & {
  refresh: () => void;
};

export interface DailyRecommendationsDeps extends DailyPoolDeps {
  sessionStore: SessionStore;
  /** Whose rows to read: the session's reader, else the device's offline reader. */
  readerOf: (state: SessionState) => string | null;
}

export const browserDailyRecommendationsDeps: DailyRecommendationsDeps = {
  sessionStore,
  readerOf: localReaderOf,
  db: readerDb,
  request: (body, signal) =>
    requestRecommendations(
      body,
      {
        fetch: (input, init) => fetch(input, init),
        fetchToken: fetchTokenResult,
      },
      signal,
    ),
  now: Date.now,
};

type LoadedPool = DailyPool & { readerId: string };

const UNREACHABLE: DailyPool = { pool: [], offline: true };

/**
 * The library's recommended short readings. The pool is read from this
 * device on mount and refetched when it is stale (see `isPoolStale`) and
 * the session can reach the server.
 */
export function useDailyRecommendations(
  deps: DailyRecommendationsDeps = browserDailyRecommendationsDeps,
): DailyRecommendations {
  const session = useSession(deps.sessionStore);
  const readerId = deps.readerOf(session);
  const canFetch = session.status === "ready";
  const { db, request, now } = deps;
  const [loaded, setLoaded] = useState<LoadedPool | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const refreshInFlight = useRef(false);
  const latestLoad = useRef(0);
  const inFlight = useRef<AbortController | null>(null);

  const load = useCallback(
    (force: boolean) => {
      if (readerId === null) return;
      if (force && refreshInFlight.current) return;
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      latestLoad.current += 1;
      const thisLoad = latestLoad.current;
      refreshInFlight.current = force;
      setRefreshing(force);
      const settle = (outcome: DailyPool) => {
        if (latestLoad.current !== thisLoad) return;
        refreshInFlight.current = false;
        setRefreshing(false);
        setLoaded({ readerId, ...outcome });
      };
      loadDailyPool(
        { readerId, canFetch, force },
        { db, request, now },
        controller.signal,
      )
        .then(settle)
        .catch(() => settle(UNREACHABLE));
    },
    [readerId, canFetch, db, request, now],
  );

  useEffect(() => {
    load(false);
    return () => {
      latestLoad.current += 1;
      refreshInFlight.current = false;
      inFlight.current?.abort();
    };
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  const pool = loaded?.readerId === readerId ? loaded : null;
  const choices = useMemo(
    () => presentRecommendations(pool?.pool ?? []),
    [pool],
  );

  if (session.status === "loading") return { status: "loading", refresh };
  if (readerId === null) {
    return {
      status: "ready",
      choices,
      offline: session.status === "unavailable",
      refreshing: false,
      refresh,
    };
  }
  if (pool === null) return { status: "loading", refresh };
  return {
    status: "ready",
    choices,
    offline: pool.offline,
    refreshing,
    refresh,
  };
}
